import { MuteText } from '@elements/Common';
import { CommonUtility } from '@utility/common';
import { AppTable, AppTableCell, AppTableRow } from '@components/AppTable';
import {
  CommonConstant,
  ShipmentTypeKeys,
  ShippingTypesIcons,
} from '@utility/constants/common';
import { Flag } from '@elements/Flag';
import { Link } from 'react-router';
import { InfoIcon, PlusIcon } from '@phosphor-icons/react';
import { useMemo, type ReactNode } from 'react';
import { Button, buttonStyles, cn, Popover } from '@skupreme/shared/ui';
import useUrlPermission from '@hooks/url-permission';
import PermissionTooltip from '@components/PermissionTooltip';
import { POShipmentItemRow } from './tabs/ShipmentTab';
import { shipmentItemColumn } from './common';

/* `color` and `textColor` are computed per destination by the PO screens, so they arrive as data. */
type AllocationSegment = {
  key: string;
  percentage?: number;
  left?: number;
  color?: string;
};

function AllocationBar({
  segments,
  compact = false,
}: {
  segments: AllocationSegment[];
  compact?: boolean;
}) {
  return (
    <div
      className={cn(
        'sk:relative sk:w-full sk:overflow-hidden sk:rounded-[5px] sk:bg-fill-secondary',
        compact ? 'sk:mx-auto sk:h-1.25' : 'sk:mx-auto sk:my-3 sk:h-5',
      )}
    >
      {segments.map(item => (
        <div
          key={item.key}
          className="sk:absolute sk:inset-y-0"
          style={{
            left: `${item.left}%`,
            width: `${item.percentage}%`,
            backgroundColor: item.color,
          }}
        />
      ))}
    </div>
  );
}

function AllocationLabel({
  color,
  bgcolor,
  children,
}: {
  color?: string;
  bgcolor?: string;
  children: ReactNode;
}) {
  return (
    <div className="sk:flex sk:items-center sk:gap-2.5" style={{ color }}>
      <span
        aria-hidden
        className="sk:size-2.5 sk:shrink-0 sk:rounded-full"
        style={{ background: bgcolor }}
      />
      {children}
    </div>
  );
}

const LABELS =
  'sk:mb-4 sk:flex sk:flex-wrap sk:items-center sk:gap-x-4 sk:gap-y-1';

/* The popovers hold the shipment table; on a phone they scroll sideways instead of overflowing. */
const POPOVER_TABLE =
  'sk:my-2 sk:overflow-x-auto sk:[&_.sk-app-table-wrap]:mt-0 sk:[&_.sk-app-table-wrap]:rounded-none';

export const allocationShipmentItemColumn = [
  {
    text: 'Destination',
    key: 'destination',
    textAlign: 'start',
  },
  {
    text: 'Shipment Type',
    key: 'shipping_type',
    textAlign: 'start',
  },
  {
    text: '',
    key: 'plan_shipment',
    textAlign: 'start',
  },
];

const CELL = 'sk:px-0! sk:py-2.5!';

function ShipmentItemRow({ allocationData, item, origin_id }) {
  const { disabled, hidden } = useUrlPermission();
  const destination = useMemo(
    () => allocationData.find(x => item?.destination?._id === x.key),
    [item, allocationData],
  );
  const linkClass = buttonStyles({
    size: 'sm',
    className: 'sk:w-full sk:no-underline',
  });

  return (
    <AppTableRow>
      <AppTableCell className={CELL}>
        <div className="sk:flex sk:items-center sk:gap-1.5">
          <span
            aria-hidden
            className="sk:size-1.5 sk:shrink-0 sk:rounded-full"
            style={{ background: destination?.color || 'transparent' }}
          />
          <Flag
            className="sk:mr-2"
            name={item?.destination?.region_name || 'US'}
          />
          {item?.destination?.name || '-'}
        </div>
      </AppTableCell>
      <AppTableCell className={CELL}>
        <div className="sk:flex sk:items-center sk:gap-2">
          <img
            alt=""
            className="sk:max-h-full sk:max-w-full"
            src={ShippingTypesIcons[item?.shipping_type]}
          />
          <span>{item?.shipping_type}</span>
        </div>
      </AppTableCell>
      <AppTableCell className={CELL}>
        {item._id ? (
          <Link to={`/app/scm/shipments/${item._id}`} className={linkClass}>
            View Shipment
          </Link>
        ) : (
          <PermissionTooltip
            disabled={disabled}
            hidden={hidden}
            position="bottom right"
          >
            {disabled ? (
              <Button size="sm" isDisabled className="sk:w-full">
                <PlusIcon aria-hidden /> Shipment
              </Button>
            ) : (
              <Link
                to={`/app/scm/shipments/create?type=${encodeURIComponent(
                  ShipmentTypeKeys.PO,
                )}&destination=${item?.destination?._id}&shipping=${
                  item?.shipping_type
                }&origin_id=${origin_id}&step=1`}
                className={linkClass}
              >
                <PlusIcon aria-hidden /> Shipment
              </Link>
            )}
          </PermissionTooltip>
        )}
      </AppTableCell>
    </AppTableRow>
  );
}

const ITEM_ROW = 'sk:mb-1.25 sk:grid sk:grid-cols-[2fr_1fr] sk:items-center';

function ItemRow({ item }) {
  return (
    <div className={ITEM_ROW}>
      <div className="sk:flex sk:min-w-0 sk:items-center sk:gap-2.5">
        <div className="sk:size-7.5 sk:shrink-0">
          <img
            alt=""
            className="sk:h-full sk:max-h-full sk:max-w-full"
            src={item.images || CommonConstant.defaultImage}
          />
        </div>
        <div className="sk:flex sk:min-w-0 sk:flex-col">
          <span className="sk:truncate" title={item.title}>
            {item.title}
          </span>
          <span className="sk:truncate sk:font-bold" title={item.sku}>
            {item.sku}
          </span>
        </div>
      </div>
      <div className="sk:text-center">
        {CommonUtility.decimalWithCommas(item.display_qty || 0)}&nbsp;/&nbsp;
        {CommonUtility.decimalWithCommas(item.shippable_qty || 0)}
      </div>
    </div>
  );
}

export function ItemAllocation({ allocationData, shipments, supplierId }) {
  return (
    <>
      <div className="sk:mb-2">
        <MuteText>Shipment Allocation by Destination</MuteText>
      </div>
      <div className={LABELS}>
        {allocationData.map(item => {
          const heading =
            item.key === 'unallocated'
              ? 'Unallocated Items'
              : `Items Allocated to ${item.name}`;

          return (
            <AllocationLabel
              key={`item-${item.key}`}
              color={item.textColor}
              bgcolor={item.color}
            >
              {item.name}
              <MuteText>{CommonUtility.roundNumber(item.percentage)}%</MuteText>
              <Popover
                aria-label={heading}
                placement="bottom start"
                trigger={
                  <Button
                    variant="ghost"
                    size="sm"
                    shape="circle"
                    aria-label={`${heading}: details`}
                  >
                    <InfoIcon aria-hidden />
                  </Button>
                }
              >
                <div className="sk:flex sk:w-[min(450px,calc(100vw-2rem))] sk:flex-col sk:p-2">
                  <div className={ITEM_ROW}>
                    <span className="sk:truncate sk:font-bold" title={heading}>
                      {heading}
                    </span>
                    <MuteText className="sk:text-center">
                      {item.key === 'unallocated'
                        ? 'Unallocated Units'
                        : 'Allocated Units'}
                    </MuteText>
                  </div>
                  {(item.items || []).map((tempItem, index) => (
                    <ItemRow
                      key={`${tempItem.item_upc}=${index}`}
                      item={tempItem}
                    />
                  ))}
                  {item.key === 'unallocated' &&
                    (shipments || []).length > 0 && (
                      <div className={POPOVER_TABLE}>
                        <div className="sk:mt-2 sk:font-bold">
                          Predicted Shipment for Unallocated Items
                        </div>
                        <AppTable
                          RowComponent={
                            // @ts-expect-error - TS2741: Property 'item' is missing in type '{ origin_id: any; allocationData: any; }' bu...
                            <ShipmentItemRow
                              origin_id={supplierId}
                              allocationData={allocationData}
                            />
                          }
                          data={shipments}
                          // @ts-expect-error - TS2322: Type '{ text: string; key: string; textAlign: string; }[]' is not assignable to ...
                          columns={allocationShipmentItemColumn}
                          noShadow
                          basic
                          // @ts-expect-error - TS2322: Type '"start"' is not assignable to type '"center" | "left" | "right" | undefine...
                          textAlign="start"
                          headerOffSet={0}
                        />
                      </div>
                    )}
                </div>
              </Popover>
            </AllocationLabel>
          );
        })}
      </div>
      <AllocationBar segments={allocationData} />
    </>
  );
}

export function ItemWiseAllocationPopup({
  allocationData,
  shipments,
  supplierId,
}) {
  return (
    <>
      <div className={LABELS}>
        {allocationData.map(item => (
          <AllocationLabel
            key={`item-${item.key}`}
            color={item.textColor}
            bgcolor={item.color}
          >
            {item.name}
            <MuteText>{CommonUtility.roundNumber(item.percentage)}%</MuteText>
          </AllocationLabel>
        ))}
      </div>
      <AllocationBar segments={allocationData} />
      <div className={POPOVER_TABLE}>
        <div className="sk:mt-2 sk:font-bold">Created Shipments</div>
        <AppTable
          RowComponent={
            // @ts-expect-error - TS2739: Type '{ origin_id: any; allocationData: any; }' is missing the following properties...
            <POShipmentItemRow
              origin_id={supplierId}
              allocationData={allocationData}
            />
          }
          data={shipments}
          // @ts-expect-error - TS2322: Type '({ text: string; key: string; width: number; textAlign: string; className?...
          columns={shipmentItemColumn}
          noShadow
          basic
          // @ts-expect-error - TS2322: Type '"start"' is not assignable to type '"center" | "left" | "right" | undefine...
          textAlign="start"
          noDataMessage={<p className="sk:ml-4">No shipment yet!</p>}
          headerOffSet={0}
        />
      </div>
    </>
  );
}

export function ItemWiseAllocation({ allocationData }) {
  return (
    <Popover
      aria-label="Allocation"
      placement="bottom"
      trigger={
        <Button
          variant="ghost"
          size="sm"
          aria-label="Allocation details"
          className="sk:mb-1.25 sk:w-full sk:gap-2 sk:px-1"
        >
          <MuteText className="sk:text-[11px] sk:break-keep">
            Allocation
          </MuteText>
          <span className="sk:flex sk:min-w-12 sk:flex-1">
            <AllocationBar segments={allocationData.allocations} compact />
          </span>
          <InfoIcon aria-hidden />
        </Button>
      }
    >
      <div className="sk:flex sk:w-[min(750px,calc(100vw-2rem))] sk:flex-col sk:p-2">
        <ItemWiseAllocationPopup
          shipments={allocationData.shipments}
          supplierId={allocationData.supplierId}
          allocationData={allocationData.allocations}
        />
      </div>
    </Popover>
  );
}
